/* ============================================================
   ONEBAR TRADING — motion runtime.
   ------------------------------------------------------------
   Three jobs, no dependencies, no GSAP, no ScrollTrigger:

     1. obtScroller()  — the single source of truth for "what is
        actually scrolling". On Horizon desktop the window does NOT
        scroll; .page-wrapper does. Three separate bugs in this theme
        came from re-deriving that wrong in three different places.

     2. Reveals — IntersectionObserver drives .obt-reveal -> .is-in.
        The failsafe it replaces used to run ONE global setTimeout that
        revealed every element on the page 2.5s after DOM-ready, which
        meant a 13,000px homepage was fully revealed before the visitor
        scrolled a pixel. The new failsafe is per element and only
        rescues elements the observer never reported on.

     3. Scroll progress — writes --p (0..1) onto [data-obt-scroll]
        while the element crosses the viewport, on rAF, so sections can
        drive scroll-linked motion from plain CSS.

   Safe to load on every page. Idempotent: re-running (Section Rendering
   API, theme editor reload) re-scans without double-observing.
   ============================================================ */
(function () {
  'use strict';

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- 1. the scroller ------------------------------------------- */

  /* Cached per call rather than once, because Horizon swaps the wrapper
     on some template transitions and a stale node silently freezes all
     scroll-linked motion. */
  function obtScroller() {
    var w = document.querySelector('#page-wrapper, .page-wrapper');
    if (w && w.scrollHeight > w.clientHeight + 4) return w;
    return document.scrollingElement || document.documentElement;
  }

  function scrollTop() {
    var s = obtScroller();
    return s === document.scrollingElement ? (window.pageYOffset || s.scrollTop) : s.scrollTop;
  }

  function scrollPercent() {
    var s = obtScroller();
    var range = s.scrollHeight - s.clientHeight;
    if (range <= 0) return 0;
    return Math.min(100, Math.max(0, (scrollTop() / range) * 100));
  }

  /* The scroll event fires on the wrapper, not on window, when the
     wrapper is the scroller. Listening to both would double-fire, so
     resolve once per subscription and re-resolve on resize. */
  function onScroll(fn, opts) {
    var target = obtScroller();
    var node = target === document.scrollingElement ? window : target;
    node.addEventListener('scroll', fn, opts || { passive: true });
    return function () { node.removeEventListener('scroll', fn); };
  }

  window.obtScroller = obtScroller;
  window.obtScrollTop = scrollTop;
  window.obtScrollPercent = scrollPercent;
  window.obtOnScroll = onScroll;

  /* ---- 2. reveals ------------------------------------------------- */

  var SEEN = 'obtRevealBound';
  var io = null;

  function markIn(el) {
    el.classList.add('is-in');
    if (el._obtFailsafe) { clearTimeout(el._obtFailsafe); el._obtFailsafe = null; }
  }

  function observer() {
    if (io) return io;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        markIn(en.target);
        io.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });
    return io;
  }

  /* True when hiding the element can no longer serve any purpose: it is on
     screen now, or the visitor has already scrolled past it. Anything still
     below the fold keeps waiting, which is the entire point of a reveal. */
  function due(el) {
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return r.top < vh;
  }

  /* Shared with the sections that carry their own reveal code. They used to
     each run a blanket setTimeout that revealed their whole section on a
     timer regardless of scroll position; this is the guarded replacement. */
  function revealVisible(items) {
    if (!items) return;
    for (var i = 0; i < items.length; i++) {
      if (REDUCE || due(items[i])) markIn(items[i]);
    }
  }
  window.obtRevealVisible = revealVisible;

  /* Per element, not global. An element only gets rescued if the observer
     never fired for it AND hiding it no longer serves a purpose. */
  function armFailsafe(el) {
    el._obtFailsafe = setTimeout(function () {
      if (!el.classList.contains('is-in') && due(el)) markIn(el);
    }, 1800);
  }

  /* An instant jump (anchor link, restored scroll position, keyboard End)
     can carry the viewport past an element without the observer ever seeing
     it intersect, which would strand it invisible forever. On every scroll
     tick, anything the visitor has already scrolled past is revealed. */
  function sweepPassed() {
    if (!io) return;
    var items = document.querySelectorAll('.obt-reveal:not(.is-in), .obt-spine:not(.is-in)');
    for (var i = 0; i < items.length; i++) {
      if (items[i].getBoundingClientRect().bottom < 0) {
        markIn(items[i]);
        io.unobserve(items[i]);
      }
    }
  }

  function scanReveals(root) {
    var items = (root || document).querySelectorAll('.obt-reveal, .obt-spine');
    if (!items.length) return;

    if (REDUCE || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    document.body.classList.add('obt-anim');
    var ob = observer();
    items.forEach(function (el) {
      if (el[SEEN]) return;
      el[SEEN] = true;
      ob.observe(el);
      armFailsafe(el);
    });
  }

  /* ---- 3. scroll-linked progress ---------------------------------- */

  var tracked = [];
  var ticking = false;

  function measure() {
    ticking = false;
    sweepPassed();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    for (var i = 0; i < tracked.length; i++) {
      var el = tracked[i];
      if (!el.isConnected) { tracked.splice(i--, 1); continue; }
      var r = el.getBoundingClientRect();
      var p;
      if (el.getAttribute('data-obt-scroll') === 'sticky') {
        /* For a tall section holding a sticky 100vh stage: 0 the moment the
           section's top meets the viewport top, 1 when its bottom does. This
           is the travel the sticky child actually experiences. The default
           mode would start such a section at ~0.36 because it already fills
           the viewport on load. */
        var travel = r.height - vh;
        p = travel <= 0 ? 0 : -r.top / travel;
      } else {
        /* 0 when the element's top edge reaches the viewport bottom,
           1 when its bottom edge reaches the viewport top. */
        var span = r.height + vh;
        p = span <= 0 ? 0 : (vh - r.top) / span;
      }
      p = p < 0 ? 0 : p > 1 ? 1 : p;
      el.style.setProperty('--p', p.toFixed(4));
    }
  }

  function request() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(measure);
  }

  function scanScroll(root) {
    var items = (root || document).querySelectorAll('[data-obt-scroll]');
    items.forEach(function (el) {
      if (tracked.indexOf(el) === -1) tracked.push(el);
    });
    if (tracked.length) request();
  }

  /* ---- wiring ------------------------------------------------------ */

  var wired = false;
  function wire() {
    if (wired) return;
    wired = true;
    onScroll(request);
    window.addEventListener('resize', request, { passive: true });
    window.addEventListener('orientationchange', request);
  }

  function init(root) {
    scanReveals(root);
    if (!REDUCE) { scanScroll(root); wire(); }
  }

  window.obtMotionScan = init;

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', function () { init(); });

  /* Theme editor + Section Rendering API: rescan the swapped subtree only. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:reorder', function () { init(); });
})();
